import React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';

import { Circle } from 'rc-progress';

import CreditLinePageTitle from './CreditLinePageTitle';
import CreditLineRecentActivitiesTable from './CreditLineRecentActivitiesTable';

import loanAnimation from '../../assets/images/Loan animation.gif';
import styles from '../../assets/styles/CreditLineDashboard.module.scss';
import '../../assets/styles/loan/Loan.scss';
import configData from '../../config/index';
import { formatUSDCurrency } from '../../helpers/format';
import { getLoanActivities, getLoanDetails } from '../../service/loan';
import Button from '../common/Button';

const CreditLineDashboard = ({ loans }) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState({});
  const [loansById, setLoansById] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLoan = async (id) => {
      try {
        const { data } = await getLoanDetails(id);
        setLoansById((prev) => [...prev.filter((loan) => loan.id !== data.id), data]);
      } catch (e) {
        console.log('error', e);
      }
    };
    loans.forEach((loan) => {
      if (loan.servicingLoanId) loadLoan(loan.servicingLoanId);
    });
  }, [loans]);

  useEffect(() => {
    const loadActivities = async (id) => {
      try {
        const res = await getLoanActivities(id);
        const activities = res.data.reverse();
        setActivities({ [id]: activities });
      } catch (e) {
        console.log('error', e);
      } finally {
        setLoading(false);
      }
    };
    loans.forEach((loan) => loadActivities(loan.servicingLoanId));
  }, [loansById]);

  const isDrowDownDisabled = (servicingLoanId) => {
    return activities[servicingLoanId]
      ?.filter((action) => action.type === 'DRAW_DOWN')
      .some((action) => action.status === 'pending');
  };
  return (
    <div className={styles.CreditLineDashboard}>
      {loading ? (
        <div className={`custom-spinner ${styles.customSpinner}`}>
          <ClipLoader color="white" loading={loading} size={70} />
        </div>
      ) : (
        <>
          {loans.some((loan) => !loan.servicingLoanId) &&
            loans.map((loan, index) => {
              return (
                <div className="loanSection" key={index}>
                  <div className="activeLoan" id={index}>
                    <div className="balance">
                      <p className="loanInfo">
                        Line of Credit{' '}
                        {configData?.dashboard?.dashboardStatusMap?.PENDING_FUNDS?.fundExactAmount
                          ? 'for'
                          : 'up to'}{' '}
                        {loan.approvedLoanAmount?.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })}
                      </p>
                      <h3 className="loanSummary">
                        {configData?.dashboard?.dashboardStatusMap?.PENDING_FUNDS?.summary}
                      </h3>
                      <img className="loanAnimation" src={loanAnimation} />
                    </div>
                  </div>
                </div>
              );
            })}

          {loansById.length > 0 && loansById[0].hasOwnProperty('lineOfCreditDto') && (
            <>
              {loansById.map((loan, index) => {
                return (
                  <div key={index}>
                    <CreditLinePageTitle>
                      Line of Credit - {loan.servicingLoanId}
                    </CreditLinePageTitle>
                    <div key={index}>
                      <section className={styles.CreditLineDashboard_contentBox}>
                        <div className={styles.CreditLineDashboard_contentBox__availableSection}>
                          <div>
                            <p className={styles.availableSection_title}>Available to Transfer</p>
                            <p className={styles.availableSection_amount}>
                              {`$ ${formatUSDCurrency(
                                loan.lineOfCreditDto?.availableToWithdraw,
                                2,
                                true
                              ).substring(1)}`}
                            </p>
                          </div>
                          <div>
                            <p className={styles.availableSection_title}>Loan Balance</p>
                            <p className={styles.availableSection_balance}>
                              {`$ ${formatUSDCurrency(
                                loan.lineOfCreditDto?.balance,
                                2,
                                true
                              ).substring(1)}`}
                            </p>
                          </div>

                          <Button
                            type="primary"
                            disabled={loan.lineOfCreditDto?.availableToWithdraw == 0}
                            onClick={() => navigate(`/account/${loan.id}/transfer-out`)}
                          >
                            Transfer
                          </Button>
                        </div>
                        <div className={styles.CreditLineDashboard_contentBox__chartSection}>
                          <div className={styles.chartSection_chart}>
                            <div className={styles.Circle}>
                              <svg>
                                <circle cx="105" cy="105" r="50"></circle>
                                <circle
                                  cx="105"
                                  cy="105"
                                  r="50"
                                  style={{
                                    '--percent':
                                      ((loan.lineOfCreditDto?.creditLimit -
                                        loan.lineOfCreditDto?.availableToDraw) /
                                        loan.lineOfCreditDto?.creditLimit) *
                                      100,
                                  }}
                                ></circle>
                              </svg>
                            </div>
                          </div>
                          <div className={styles.chartSection_listOfValues}>
                            <ul>
                              <li>
                                <p>Loan/Credit Limit</p>
                                <p className={styles.listOfValues_value}>
                                  {formatUSDCurrency(loan.lineOfCreditDto?.creditLimit, 2, true)}
                                </p>
                              </li>
                              <li>
                                <p className={styles.listOfValues_balance}>Amount Drawn Down</p>
                                <p className={styles.listOfValues_value}>
                                  {formatUSDCurrency(
                                    loan.lineOfCreditDto?.amountDrawnDown,
                                    2,
                                    true
                                  )}
                                </p>
                              </li>
                              <li>
                                <p className={styles.listOfValues_creditLimit}>
                                  Available to Draw Down
                                </p>
                                <p className={styles.listOfValues_value}>
                                  {formatUSDCurrency(
                                    loan.lineOfCreditDto?.availableToDraw,
                                    2,
                                    true
                                  )}
                                </p>
                              </li>
                            </ul>
                            <div className={styles.chartSection_buttonGroup}>
                              <Button
                                type="primary"
                                disabled={isDrowDownDisabled(loan.servicingLoanId, index)}
                                onClick={() => navigate(`/account/${loan.id}/draw-down`)}
                              >
                                Draw Down
                              </Button>
                            </div>
                          </div>
                        </div>
                      </section>

                      {activities[loan.servicingLoanId]?.length > 0 && (
                        <>
                          <h3 className={styles.CreditLineDashboard_tableTitle}>Recent Activity</h3>
                          <div className={styles.CreditLineDashboard_activitiesTableWrapper}>
                            <CreditLineRecentActivitiesTable
                              activities={activities[loan.servicingLoanId]}
                              id={loan.servicingLoanId}
                            />
                          </div>
                          {activities[loan.servicingLoanId]?.length > 0 && (
                            <div className={styles.CreditLineDashboard_allActivities}>
                              <Button
                                type="default"
                                onClick={() =>
                                  navigate(`/account/${loan.servicingLoanId}/recent-activities`)
                                }
                              >
                                View All
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CreditLineDashboard;
